import React, { useState, useEffect, useCallback } from "react";
import { TrashIcon, PlusCircleIcon } from "@primer/octicons-react";
import axios from "axios";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Swal from "sweetalert2";

import greenTriangle from "./../../../../components/img/green-triangle.png";
import redTriangle from "./../../../../components/img/red-triangle.png";

import { BASENAME, REST_API } from "./../../../../services/constants";
import authHeader from "../../../../services/auth-header";

export default function ManageTopics() {
  const [topics, setTopics] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchTopics = useCallback(async () => {
    try {
      const headers = await authHeader();
      const response = await axios.get(`${REST_API}/api/secure/topics/`, {
        headers,
      });

      console.log("Topics API:", response.data);

      const sortedTopics = response.data.sort(
        (a, b) => a.order_nr - b.order_nr,
      );
      setTopics(sortedTopics);
      setAccessDenied(false);
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setAccessDenied(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Verbindung fehlgeschlagen",
          text: "Es konnte keine Verbindung zum Server hergestellt werden.",
        });
      }
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  const updateTopic = async (topic) => {
    const headers = await authHeader();
    return axios.patch(
      `${REST_API}/api/secure/topics/${topic.id}`,
      {
        id: topic.id,
        title: topic.title,
        info: topic.info,
        mandatory: topic.mandatory,
        order_nr: topic.order_nr,
      },
      { headers },
    );
  };

  const getNextFreeOrderNr = () => {
    if (!topics || topics.length === 0) return 1;
    const lastTopic = topics[topics.length - 1];
    return (lastTopic?.order_nr || 0) + 1;
  };

  const isOrderNrAlreadyTaken = (orderNr) => {
    return topics.some((topic) => topic.order_nr === orderNr);
  };

  const createNewTopic = () => {
    const nextFreeOrderNr = getNextFreeOrderNr();

    Swal.fire({
      icon: "info",
      title: "Thema erstellen",
      showCancelButton: true,
      html: `
        <label for="swal-input1">Gebe einen passenden Namen an</label>
        <input style="margin-bottom: 25px" id="swal-input1" class="swal2-input">
        <label for="swal-input2">Position in Reihenfolge</label>
        <input id="swal-input2" class="swal2-input" type="number" value="${nextFreeOrderNr}">
      `,
      focusConfirm: false,
      preConfirm: () => {
        const title = document.getElementById("swal-input1").value.trim();
        const orderNr = document.getElementById("swal-input2").value.trim();

        if (!title)
          return Swal.showValidationMessage("Der Name darf nicht leer sein");
        if (title.length < 5)
          return Swal.showValidationMessage(
            "Der Name muss mindestens 5 Zeichen haben",
          );
        if (title.length > 90)
          return Swal.showValidationMessage(
            "Der Name darf nicht länger als 90 Zeichen sein",
          );

        if (!orderNr)
          return Swal.showValidationMessage("Die Nummer darf nicht leer sein");
        const orderNrValue = parseFloat(orderNr);

        if (isNaN(orderNrValue))
          return Swal.showValidationMessage(
            "Position in Reihenfolge muss eine Zahl sein",
          );
        if (isOrderNrAlreadyTaken(orderNrValue))
          return Swal.showValidationMessage(
            "Position in Reihenfolge bereits besetzt, bitte wähle eine andere",
          );
        if (orderNrValue < 1)
          return Swal.showValidationMessage("Die Zahl muss größer als 0 sein");

        return { title, orderNrValue };
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const headers = await authHeader();
          await axios.post(
            `${REST_API}/api/secure/topics/`,
            { title: result.value.title, order_nr: result.value.orderNrValue },
            { headers },
          );
          fetchTopics();
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Fehler",
            text: "Das Thema konnte nicht erstellt werden.",
          });
        }
      }
    });
  };

  const deleteTopic = (title, id) => {
    Swal.fire({
      icon: "warning",
      title: "Thema löschen?",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      text:
        "Sicher, dass du das Thema löschen möchtest? Dies kann nicht rückgängig gemacht werden.",
      input: "text",
      inputLabel: `Tippe "${title}" als Bestätigung ein`,
      inputValidator: (value) => {
        if (value !== title) {
          return "Bitte gebe den exakten Themennamen als Bestätigung ein.";
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const headers = await authHeader();
          await axios.get(`${REST_API}/api/secure/topics/delete/${id}`, {
            headers,
          });
          fetchTopics();
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Fehler",
            text: "Das Thema konnte nicht gelöscht werden.",
          });
        }
      }
    });
  };

  const swapTopicOrder = async (index1, index2) => {
    try {
      const topic1 = { ...topics[index1] };
      const topic2 = { ...topics[index2] };

      const tempOrder = topic1.order_nr;

      await updateTopic({ ...topic1, order_nr: 999999 });

      await updateTopic({ ...topic2, order_nr: tempOrder });
      await updateTopic({ ...topic1, order_nr: topic2.order_nr });

      await fetchTopics();
    } catch (error) {
      console.error("Fehler beim Ändern der Reihenfolge:", error);
      Swal.fire({ title: "Fehler beim Update des Themas" });
    }
  };

  const incrementTopicOrderNr = (index) => {
    if (index >= topics.length - 1) {
      Swal.fire({
        title: "Das Thema kann nicht weiter nach unten verschoben werden",
      });
      return;
    }
    swapTopicOrder(index, index + 1);
  };

  const decrementTopicOrderNr = (index) => {
    if (index <= 0) {
      Swal.fire({
        title: "Das Thema kann nicht weiter nach oben verschoben werden",
      });
      return;
    }
    swapTopicOrder(index, index - 1);
  };

  return (
    <>
      <br />
      {accessDenied ? (
        <div className="text-danger">Keine Berechtigung</div>
      ) : topics.length === 0 ? (
        <div className="text-muted">Keine Daten</div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>ID</th>
              <th>Position in Reihenfolge</th>
              <th>Titel</th>
              <th>Obligatorisch</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {topics.map((topic, index) => (
              <tr key={topic.id || index}>
                <td>{topic.id}</td>
                <td>{topic.order_nr}</td>
                <td>{topic.title}</td>
                <td>{topic.mandatory ? "Ja" : "Nein"}</td>
                <td>
                  <Button
                    href={`#${BASENAME}admin-dashboard/topics/${topic.id}`}
                    className="mr-2 mb-2"
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => deleteTopic(topic.title, topic.id)}
                    className="mr-2 mb-2"
                  >
                    <TrashIcon size={16} /> Löschen
                  </Button>
                  <Button
                    variant="secondary"
                    className="mr-2 mb-2"
                    onClick={() => decrementTopicOrderNr(index)}
                  >
                    <img
                      src={greenTriangle}
                      height="32"
                      width="32"
                      alt="Nach oben"
                    />
                  </Button>
                  <Button
                    variant="secondary"
                    className="mr-2 mb-2"
                    onClick={() => incrementTopicOrderNr(index)}
                  >
                    <img
                      src={redTriangle}
                      height="32"
                      width="32"
                      alt="Nach unten"
                    />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Button onClick={createNewTopic}>
        <PlusCircleIcon size={18} className="mr-2" /> Thema erstellen
      </Button>
    </>
  );
}
